import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableCell, TableRow, TableHead } from '@/components/ui/table';
import { tableHeadClass, tableCellClass } from '@/components/ui/table-styles';
import { AutoCompletePortal, type Option } from '@/components/ui/autocomplete-portal';
import { Plus, ChevronRight, Search, X, ChevronsLeft, ChevronLeft, ChevronsRight, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import PaginationButton from '@/components/ui/pagination-button';
import { ListPurchaseRequestsResponse } from '@/gen/purchase_request/list_purchase_requests_pb';
import { ItemType } from '@/gen/inventory/common_pb';
import { PurchaseRequestLineItem } from '@/gen/purchase_request/common_pb';

interface PurchaseRequestItemsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseRequests: ListPurchaseRequestsResponse;
  handleBulkAdd: (items: Array<{
      product: Option | undefined;
      bonus: string;
      quantity: string;
      intendedCost: string;
      productInput: string;
      notes?: string;
      purchaseRequestId?: string[];
  }>) => void
}

const PurchaseRequestItemsModal: React.FC<PurchaseRequestItemsModalProps> = ({ open, onOpenChange, purchaseRequests, handleBulkAdd }) => {
  const [selectedRequestId, setSelectedRequestId] = React.useState<string | null>(null);
  const [searchInput, setSearchInput] = React.useState('');
  // Track added items by value
  const [addedItems, setAddedItems] = React.useState<Array<{
    product: Option | undefined;
    bonus: string;
    quantity: string;
    intendedCost: string;
    productInput: string;
    notes?: string;
    purchaseRequestId?: string[];
  }>>([]);


  // Filter items by search input
  const filteredItems = purchaseRequests.purchaseRequests.find(i => i.id === selectedRequestId)?.items.filter(item =>
    item.tradeName.toLowerCase().includes(searchInput.toLowerCase())
  ) || [];

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 7;
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Pagination logic for ellipsis
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(currentPage + 1, totalPages - 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  // Handler for adding an item
  const handleAddItem = (item: PurchaseRequestLineItem) => {
    setAddedItems(prev => {
      const existingItem = prev.find(addedItem => addedItem.product?.value === item.masterId && addedItem.quantity === item.quantity.toString() && addedItem.productInput === item.tradeName && addedItem.purchaseRequestId?.includes(selectedRequestId || ''));
      if (existingItem) {
        return prev; // Item already added, do nothing
      }

      // If already added, but with a different request ID, add the quantity to the existing item
      const existingWithDifferentRequest = prev.find(addedItem => addedItem.product?.value === item.masterId && addedItem.productInput === item.tradeName);
      if (existingWithDifferentRequest) {
        return prev.map(addedItem => {
          if (addedItem.product?.value === item.masterId) {
            return {
              ...addedItem,
              quantity: (parseInt(addedItem.quantity) + item.quantity).toString(),
              purchaseRequestId: addedItem.purchaseRequestId ? [...addedItem.purchaseRequestId, selectedRequestId || ''] : [selectedRequestId || '']
            };
          }
          return addedItem;
        });
      }

      const newItem = {
        product: { value: item.masterId, label: item.tradeName, type: item.itemType.toString() },
        bonus: '',
        quantity: item.quantity.toString(),
        intendedCost: '',
        productInput: item.tradeName,
        notes: item.notes || '',
        purchaseRequestId: selectedRequestId ? [selectedRequestId] : undefined
      };
      return [...prev, newItem];
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] h-[90vh] max-w-none max-h-none p-0 flex flex-col gap-0">
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="text-lg">Select Multiple Items</DialogTitle>
          <DialogClose asChild>
            <Button variant="ghost" className="absolute right-2 top-2">✕</Button>
          </DialogClose>
        </DialogHeader>
        <div className="flex-1 flex flex-col min-h-0 h-full">
          <div className="flex flex-1 min-h-0 h-full">
            <div className="w-1/4 min-w-[150px] max-w-xs bg-gray-50 border-r h-full p-0 flex flex-col min-h-0 overflow-hidden">
              <div className="font-semibold px-4 py-2 border-b">Purchase Requests</div>
              <div className="flex-1 min-h-0 overflow-y-auto h-full">
                {purchaseRequests.purchaseRequests.map((req, idx) => (
                  <Button
                    key={req.id}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium border-0 border-b border-gray-200 bg-transparent transition-colors ${selectedRequestId === req.id ? 'text-black' : 'text-gray-400'}`}
                    style={{ outline: 'none' }}
                    onClick={() => setSelectedRequestId(req.id)}
                  >
                    <span>{req.requestNumber}</span>
                    {selectedRequestId === req.id && (
                      <ChevronRight className="w-4 h-4 ml-2 transition-colors text-black" />
                    )}
                  </Button>
                ))}
              </div>
            </div>
            {/* Main content */}
            <div className="flex-1 min-h-0 h-full px-4 py-2 overflow-auto flex flex-col">
              <div className="font-semibold mb-4">Items List</div>
              <div className="mb-4 max-w-xs">
                <div className="relative flex items-center">
                  <Search className="absolute left-2 w-4 h-4 text-gray-800 pointer-events-none z-10" />
                  <Input
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    placeholder="Search product..."
                    className="pl-8 pr-8 h-9 text-sm border-[0.5px]"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => setSearchInput('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 focus:outline-none z-10"
                      tabIndex={-1}
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-auto">
                <div className="border border-slate-200 rounded-lg bg-white">
                  <Table className="text-sm">
                    <TableHeader>
                      <TableRow className="text-sm">
                        <TableHead className={tableHeadClass}>SL No.</TableHead>
                        <TableHead className={tableHeadClass + " min-w-[60%] w-[60%]"}>Product Name</TableHead>
                        <TableHead className={tableHeadClass}>Quantity</TableHead>
                        <TableHead className={tableHeadClass}></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedItems.map((item, idx) => {
                        const isAdded = addedItems.some(addedItem => addedItem.product?.value === item.masterId && addedItem.productInput === item.tradeName && addedItem.purchaseRequestId?.includes(selectedRequestId || ''));
                        const realIdx = (currentPage - 1) * itemsPerPage + idx;
                        return (
                          <TableRow className="font-medium" key={item.masterId}>
                            <TableCell className={tableCellClass}>{realIdx + 1}</TableCell>
                            <TableCell className={tableCellClass + " "}>{item.tradeName}</TableCell>
                            <TableCell className={tableCellClass}>{item.quantity}</TableCell>
                            <TableCell className={tableCellClass}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1"
                                onClick={() => {
                                  if (isAdded) {
                                    // Remove item from added items only for the selected request
                                    // If the item is added with multiple request IDs, just remove the current request ID
                                    setAddedItems(prev => {
                                      return prev
                                        .map(addedItem => {
                                          if (addedItem.product?.value === item.masterId && addedItem.productInput === item.tradeName) {
                                            const updatedRequestIds = addedItem.purchaseRequestId?.filter(id => id !== selectedRequestId) || [];
                                            if (updatedRequestIds.length === 0) {
                                              // If no request IDs left, remove the item completely
                                              return null;
                                            }
                                            // Reduce the quantity if needed
                                            const newQuantity = parseInt(addedItem.quantity) - item.quantity;
                                            if (newQuantity <= 0) {
                                              // If quantity goes to zero or less, remove the item completely
                                              return null;
                                            }
                                            // Otherwise, return the updated item with reduced quantity and updated request IDs
                                            return {
                                              ...addedItem,
                                              quantity: newQuantity.toString(),
                                              purchaseRequestId: updatedRequestIds
                                            };
                                          }
                                          return addedItem;
                                        })
                                        .filter((item): item is typeof prev[number] => item !== null); // Type guard to remove nulls
                                    });
                                  } else {
                                    handleAddItem(item);
                                  }
                                }}
                              >
                                {isAdded ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {isAdded ? 'Remove' : 'Add'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between p-2 text-sm border-t bg-white rounded-b-lg">
                      <p>{filteredItems.length} <span className="text-gray-500 font-light">count</span></p>
                      <div className="flex items-center gap-1">
                        <PaginationButton
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        >
                          <ChevronsLeft className="h-4 w-4" />
                        </PaginationButton>
                        <PaginationButton
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </PaginationButton>
                        {getPageNumbers().map((page, idx) => (
                          page === '...'
                            ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                            : <PaginationButton
                                key={page}
                                active={currentPage === page}
                                onClick={() => setCurrentPage(page as number)}
                                disabled={currentPage === page}
                              >
                                <span className="text-sm">{page}</span>
                              </PaginationButton>
                        ))}
                        <PaginationButton
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </PaginationButton>
                        <PaginationButton
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronsRight className="h-4 w-4" />
                        </PaginationButton>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="p-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="mr-2">Cancel</Button>
          <Button
            variant="default"
            onClick={() => {
              handleBulkAdd(addedItems);
              onOpenChange(false);
            }}
          >
            Add Selected Items
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseRequestItemsModal;
